import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { CURRENCIES } from "./MembershipPage";

const TIERS = [
  { id: "free", name: "Free", note: "Start here" },
  { id: "tourist", name: "Tourist", note: "Visit & experience" },
  { id: "returnee", name: "Returnee", note: "Relocate & settle" },
  { id: "expat", name: "Expat", note: "Work postings" },
  { id: "investor", name: "Investor", note: "Research & invest" },
];

const ROWS = [
  { label: "Free General A–Z Guide & public resources", tiers: { free: true, tourist: true, returnee: true, expat: true, investor: true } },
  { label: "Free Nigerian Airport Guide PDF", tiers: { free: true, tourist: true, returnee: true, expat: true, investor: true } },
  { label: "Members-only specialist A–Z collection", tiers: { tourist: "Tourist A–Z", returnee: "Returnee A–Z", expat: "Expat A–Z", investor: "Investor A–Z" } },
  { label: "JÀWÀ WhatsApp Community — questions, connections & networking", tiers: { tourist: true, returnee: true, expat: true, investor: true } },
  { label: "Priority VIP access to eligible JÀWÀ events & experiences", tiers: { tourist: true, returnee: true, expat: true, investor: true } },
  { label: "Exclusive member pricing on selected eligible services", tiers: { tourist: true, returnee: true, expat: true, investor: true } },
];

export default function MembershipsPage() {
  const [plans, setPlans] = useState([]);
  const { currency, setCurrency } = useCurrency();
  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "";

  useEffect(() => {
    api.get("/plans").then((res) => setPlans(res.data)).catch(() => {});
  }, []);

  const price = (audience, period) => {
    if (audience === "free") return "0";
    const pl = plans.find((p) => p.audience === audience && p.period === period);
    const v = pl?.prices?.[currency];
    return v != null ? v.toLocaleString() : "—";
  };

  return (
    <div className="bg-jawa-ivory" data-testid="memberships-page">
      <Nav />
      <Seo
        title="Memberships — Join the JÀWÀ Circle | JÀWÀ Africa"
        description="Four JÀWÀ memberships for tourists, investors, returnees and expats — members-only A–Z collections, the WhatsApp community and member pricing."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionHeading overline="Memberships" title="Choose your JÀWÀ." />
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
              One free tier to start, four specialist memberships when you're ready. Prices are the agreed
              JÀWÀ launch prices in each currency — pick your currency above.
            </p>
          </div>
          <div className="flex gap-1" data-testid="memberships-currency-selector">
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                data-testid={`memberships-currency-${c.code.toLowerCase()}`}
                className={`px-4 py-2 text-xs font-semibold transition-colors ${
                  currency === c.code ? "bg-jawa-espresso text-jawa-ivory" : "border border-jawa-espresso/20 text-jawa-espresso/60 hover:text-jawa-espresso"
                }`}
              >
                {c.code}
              </button>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mt-12 overflow-x-auto">
          <table className="w-full min-w-[760px] border border-jawa-stone bg-white" data-testid="membership-comparison">
            <thead>
              <tr className="border-b border-jawa-stone text-left">
                <th className="p-5 text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">What's included</th>
                {TIERS.map((t) => (
                  <th key={t.id} className={`p-5 ${t.id === "tourist" ? "bg-jawa-turquoise/10" : ""}`}>
                    <p className="font-serif text-xl font-medium text-jawa-espresso">{t.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-jawa-espresso/50">{t.note}</p>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-jawa-stone">
                <td className="p-5 text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">Monthly / Annual ({currency})</td>
                {TIERS.map((t) => (
                  <td key={t.id} className={`p-5 ${t.id === "tourist" ? "bg-jawa-turquoise/10" : ""}`} data-testid={`membership-price-${t.id}`}>
                    <p className="font-serif text-lg text-jawa-espresso">
                      {symbol}{price(t.id, "monthly")} <span className="text-xs text-jawa-espresso/50">/ mo</span>
                    </p>
                    <p className="text-xs text-jawa-espresso/60">{symbol}{price(t.id, "annual")} / yr</p>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} className="border-b border-jawa-stone/70">
                  <td className="p-5 text-sm text-jawa-espresso/75">{r.label}</td>
                  {TIERS.map((t) => {
                    const v = r.tiers[t.id];
                    return (
                      <td key={t.id} className={`p-5 ${t.id === "tourist" ? "bg-jawa-turquoise/10" : ""}`}>
                        {v === true ? (
                          <BadgeCheck className="h-5 w-5 text-jawa-turquoise" />
                        ) : typeof v === "string" ? (
                          <span className="text-xs font-semibold text-jawa-coral">{v}</span>
                        ) : (
                          <Minus className="h-4 w-4 text-jawa-espresso/25" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </FadeUp>

        <FadeUp className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="membership-tier-cards">
          {TIERS.map((t) => (
            <Link
              key={t.id}
              to={t.id === "free" ? "/guides" : `/membership/${t.id}`}
              data-testid={`membership-tier-${t.id}`}
              className="group border border-jawa-stone bg-white p-6 transition-colors hover:border-jawa-coral"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-coral">{t.note}</p>
              <p className="mt-2 font-serif text-2xl font-medium text-jawa-espresso group-hover:text-jawa-coral">
                {t.id === "free" ? "Free Membership" : `${t.name} Membership`}
              </p>
              <p className="mt-2 text-sm text-jawa-espresso/60">
                {t.id === "free" ? "£0 — free guides & resources" : `${symbol}${price(t.id, "monthly")}/mo · ${symbol}${price(t.id, "annual")}/yr`}
              </p>
            </Link>
          ))}
        </FadeUp>

        <FadeUp className="mt-12 max-w-3xl space-y-3 text-xs leading-relaxed text-jawa-espresso/55" data-testid="membership-terms">
          <p>
            Pay securely online by card (charged in naira via Paystack at the naira plan price) or request bank-transfer
            details via our official WhatsApp after registering — bank details are never published on this website.
            Access is activated once your payment or transfer is verified.
          </p>
          <p>
            Prices above are the agreed JÀWÀ launch prices per currency — not estimates. Memberships do not renew
            automatically — request a renewal from your member area before expiry. Priority VIP access means early
            access to eligible events and offers; it is not a guaranteed ticket, and member pricing applies to
            selected eligible services only. Concierge services, consultations and the flagship A–Z guide PDFs are
            separate purchases, not membership entitlements.
          </p>
          <p>
            The WhatsApp Community is for questions, useful information and networking; joining details are
            shared with paid members after activation.
          </p>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
